import {
  ApplicationRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
  ViewChild
} from "@angular/core";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs";
import { Building } from "src/components/shared-components/class/general-classes/building";
import { Plant } from "src/model/class/general classes/plant";
import { UsersRole } from "src/model/enum/enumDigitalPlace";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { EventService } from "src/service/event.service";
import { listBuilding } from "src/components/shared-components/state/selectors/building.selector";
import { listPlant } from "src/state/selectors/plant.selectors";
import * as L from "leaflet";
import { addBaseMapLayer } from "src/model/class/general classes/base-map-layer";
import { formatBuildingAddress } from "src/model/class/general classes/building-address";
import { HttpClient } from "@angular/common/http";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { AreaService } from "src/service/area.service";
import { SupervisionService } from "src/service/supervision.service";
import { SystemRouterService } from "src/service/system-router.service";
import { AlertService } from "src/service/alert.service";
import { TranslateService } from "@ngx-translate/core";
import { currentBuildingChangeSelectionAction } from "src/components/shared-components/state/actions/building.action";

@Component({
    selector: "app-building-list-associated",
    templateUrl: "./building-list.component.html",
    styleUrls: ["./building-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BuildingListComponent implements OnInit, OnDestroy, iEvent {
  //#region Subcriptions to "_store.select(...)" Observables
  private _selectListBuildingSub: Subscription = null;
  /** Switches the language of the map place names; follows the app language */
  private _setMapLanguage: (language: string) => void = null;
  private _mapLanguageSub: Subscription = null;
  /** Marker of each building on the map, to open its popup from the list */
  private _markersByBuilding = new Map<number, L.Marker>();
  private _mapIsInserted: boolean = false;
  /** Redraws the map whenever its box changes size (layout, page transition, List/Map switch) */
  private _mapResizeObserver: ResizeObserver = null;
  //#endregion

  public _eventSubscriptions: Array<Subscription> = null;
  public map: L.Map;
  public listBuildingsFiltered: Array<Building> = new Array<Building>();
  public iconMarkup: string = '<ion-icon name="pin"></ion-icon>';
  public customIcon: string = L.icon({
    iconUrl: "../../assets/img/pin.svg",
    iconSize: [25, 25],
  });

  public screenWidth: number = 0;
  /** Phones show either the list or the map */
  public mobileView: "list" | "map" = "list";
  /** Building picked from the list (highlighted, its marker popup is open) */
  public selectedBuildingId: number = null;

  @ViewChild("mapContainer") mapContainer: ElementRef<HTMLDivElement>;

  @Input() CurrentValueTab: number;
  @Input() listBuildings: Array<Building>;
  @Input() role: UsersRole;
  /** Show "Add building" in the empty state */
  @Input() canAddBuilding: boolean = false;
  @Output() reloadBuildingsEvent = new EventEmitter();
  @Output() addBuilding = new EventEmitter<void>();

  constructor(
    private _eventService: EventService,
    private _app: ApplicationRef,
    private _store: Store,
    private _ngZone: NgZone,
    private _http: HttpClient,
    private _areaService: AreaService,
    private _supervisionService: SupervisionService,
    private _systemRouterService: SystemRouterService,
    private _alertService: AlertService,
    private _translateService: TranslateService
  ) {
  }

  /** Wide screens show the list and the map side by side */
  public get isWideScreen(): boolean {
    return this.screenWidth >= 950;
  }

  /** The map is on screen: always on wide screens, on request on phones */
  private get isMapVisible(): boolean {
    return this.isWideScreen || this.mobileView === "map";
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['listBuildings']) {
      this.listBuildingsFiltered=this.listBuildings;
    }
    if (this.isMapVisible) {
      this.loadMaps();
    }
  }

  ngOnInit() {
    this.screenWidth = window.innerWidth;
    this.listBuildingsFiltered = this.listBuildings;
  }

  ngOnDestroy() {
    this.unsubscribeStoreSelect();
    this._mapLanguageSub?.unsubscribe();
    this._mapResizeObserver?.disconnect();
    // Free the map (and its WebGL context: browsers keep only a few, older ones go blank)
    this.map?.remove();
    this.map = null;
    this._mapIsInserted = false;
  }

  /** Get screen width on resize event*/
  @HostListener('window:resize')
  onWindowResize(): void {
    this.screenWidth = window.innerWidth;
    if (this.isMapVisible) {
      this.loadMaps();
    }
  }

  ngAfterViewInit() {
    // A map created while its box has no size stays blank: redraw it as soon as the box changes
    this._mapResizeObserver = new ResizeObserver(() => {
      if (this.map && this.mapContainer.nativeElement.offsetWidth > 0) {
        this.map?.invalidateSize();
      }
    });
    this._mapResizeObserver.observe(this.mapContainer.nativeElement);

    if (this.isMapVisible) {
      this.loadMaps();
    }
  }

  /** Phones: back to the list */
  public onClickShowList(): void {
    this.mobileView = "list";
  }

  /** Phones: show the map (created the first time it is opened) */
  public onClickShowMap(): void {
    this.mobileView = "map";
    this.loadMaps();
  }

  //Load or reload maps
  private loadMaps() {
    // The map container exists only once the view is rendered
    if (!this.mapContainer) {
      return;
    }
    if (!this._mapIsInserted) {
      this.map = L.map(this.mapContainer.nativeElement);
      this.map.setView([AppSettings.LAT_IT, AppSettings.LON_IT], 5.5);
      this._setMapLanguage = addBaseMapLayer(this.map, this._translateService.getCurrentLang() || this._translateService.getFallbackLang() || "it");
      this._mapLanguageSub = this._translateService.onLangChange.subscribe((event) =>
        this._setMapLanguage?.(event.lang)
      );
      this._mapIsInserted = true;
    }

    this.removeLayer();
    this.addMarkerOnMap();

    setTimeout(() => {
      this.map?.invalidateSize();
    }, 500);
  }

  /**
   * Handled items load after type on searchbar
   * @param event
   */
  public onSearchChange(event: Event) {
    
    const searchTerm =
      (event.target as HTMLIonSearchbarElement).value?.toLowerCase().trim() ?? "";
    if (searchTerm !== "") {
      this.listBuildingsFiltered = this.listBuildings.filter((building) =>
        building.name.toLowerCase().includes(searchTerm)
      );
    } else {
      this.listBuildingsFiltered = this.listBuildings;
    }
  }

  /**
   * Add marker on map
   */
  private addMarkerOnMap() {
    for (let i = 0; i < this.listBuildings.length; i++) {
      if (
        !isNaN(Number(this.listBuildings[i].latitude)) &&
        !isNaN(Number(this.listBuildings[i].longitude))
      ) {
        const marker = L.marker(
          [this.listBuildings[i].latitude, this.listBuildings[i].longitude],
          {
            icon: this.customIcon,
          }
        )
          .addTo(this.map)
          .bindPopup(this.createBuildingPopup(this.listBuildings[i]), {
            className: "building-popup",
            minWidth: 220,
          });
        this._markersByBuilding.set(this.listBuildings[i].id, marker);
      }
    }
  }

  /**
   * Content of a marker popup: name, address and the button that opens the building.
   * Built with DOM nodes (textContent), so building data cannot inject markup.
   *
   * @param building building of the marker
   */
  private createBuildingPopup(building: Building): HTMLElement {
    const container = document.createElement("div");
    container.className = "building-popup__content";

    const title = document.createElement("div");
    title.className = "building-popup__title";
    title.textContent = building.name;
    container.appendChild(title);

    const address = formatBuildingAddress(building);
    if (address) {
      const addressRow = document.createElement("div");
      addressRow.className = "building-popup__address";
      addressRow.textContent = address;
      container.appendChild(addressRow);
    }

    const button = document.createElement("button");
    button.type = "button";
    button.className = "building-popup__button";
    button.textContent = this._translateService.instant("dictionary.openAction");
    button.addEventListener("click", () => {
      this._ngZone.run(() => this.enterBuilding(building));
    });
    container.appendChild(button);

    return container;
  }

  /**
   * Open a building from its map marker: same steps as tapping it in the list
   *
   * @param building building to open
   */
  private async enterBuilding(building: Building): Promise<void> {
    try {
      const area = await this._areaService.getAreaById(building.idAreaLocal);
      this._store.dispatch(
        currentBuildingChangeSelectionAction({
          selectedBuilding: building,
          defaultBuilding: area.default,
        })
      );
      this._supervisionService.reloadServiceData();
      this._systemRouterService.navigateToDigitalPlaceHome();
    } catch {
      this._alertService.presentAlertSomethingWrong();
    }
  }

  /**
   * Remove layer and marker
   */
  private removeLayer() {
    this._markersByBuilding.clear();
    this.map.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        this.map.removeLayer(layer);
      }
    });
  }

  //Zooms on the map, on a specific building
  public selectBuilding(idBuilding) {
    const building = this.listBuildings.find((b) => b.id === idBuilding);
    if (!building) {
      return;
    }
    this.selectedBuildingId = idBuilding;

    // Phones: switch to the map first, it needs its size before zooming
    const mapWasHidden = !this.isMapVisible;
    if (mapWasHidden) {
      this.onClickShowMap();
    }
    setTimeout(() => {
      this.map?.invalidateSize();
      this.map?.setView([building.latitude, building.longitude], 16);
      // Show which one it is: open its marker popup (name, address, "Open")
      this._markersByBuilding.get(idBuilding)?.openPopup();
    }, mapWasHidden ? 50 : 0);
  }

  /**
   * Emit event to reload list of buildings
   */
  public reloadBuildings() {
    this.reloadBuildingsEvent.emit();
  }

  //#region Subscribe to event-service observable ///
  public subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnQRPlantScanned.subscribe((plant: Plant) => {
        plant.LanPort = "80";
        plant.WanPort = "80";

        this._app.tick();
      })
    );
  }

  public unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  //#region Unsubscribe from "_store.selec(...)" Observable

  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectListBuildingSub) {
      this._selectListBuildingSub.unsubscribe();
    }
  }

  //#endregion
}
